import createAuthRefreshInterceptor from 'axios-auth-refresh'
import { plantarApi, aliareApi, notificationHubApi, plantarApiV2, vistraBiApi, newPlantarApi } from './appAPIs'
import { toast } from '@/common/util'
import { extractNotifications } from '@/common/apiError'
import { wasSentWithOldToken } from '@/common/authenticationLog'
import { useSystemStore, useAuthStore, useVistraStore } from '@/stores'

export const setupEndPoints = () => {
  const authStore = useAuthStore()
  const vistraStore = useVistraStore()

  const interceptorResponseOnError = (error) => {
    const { success } = error.response?.data || {}
    const errors = extractNotifications(error)

    if (!success && errors.length > 0 && !error.config?.silent) {
      errors?.map((msg) => {
        toast.error({
          message: msg
        })
      })
    }

    return Promise.reject(error)
  }

  const interceptorRequest = async (config) => {
    if (authStore.loggedIn) {
      config.headers['Authorization'] = `${authStore.token.token_type} ${authStore.token.access_token}`
      config.headers['X-Tenant'] = authStore.getActiveTentant
    }
    config.headers['Accept-Language'] = useSystemStore().clientSettings.lang
    return config
  }

  // Interceptor de requisição
  plantarApi.interceptors.request.use(
    interceptorRequest,
    (error) => Promise.reject(error)
  )

  plantarApiV2.interceptors.request.use(
    interceptorRequest,
    (error) => Promise.reject(error)
  )

  newPlantarApi.interceptors.request.use(
    interceptorRequest,
    (error) => Promise.reject(error)
  )

  aliareApi.users.interceptors.request.use(
    async (config) => {
      if (authStore.loggedIn) {
        config.headers['Authorization'] = `${authStore.token.token_type} ${authStore.token.access_token}`
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  notificationHubApi.interceptors.request.use(
    async (config) => {
      if (authStore.loggedIn) {
        config.headers['Authorization'] = `${authStore.token.token_type} ${authStore.token.access_token}`
        config.headers['X-Tenant'] = authStore.getActiveTentant
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  vistraBiApi.interceptors.request.use(
    async (config) => {
      if (vistraStore.vistraToken) {
        config.headers['Authorization'] = `Bearer ${vistraStore.vistraToken}`
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  aliareApi.portal.interceptors.request.use(
    async (config) => {
      if (authStore.loggedIn) {
        config.headers['Authorization'] = `${authStore.token.token_type} ${authStore.token.access_token}`
        config.headers['X-Tenant'] = authStore.getActiveTentant
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // Interceptor de resposta
  plantarApi.interceptors?.response.use(
    (response) => response?.data,
    interceptorResponseOnError
  )

  plantarApiV2.interceptors?.response.use(
    (response) => response?.data,
    interceptorResponseOnError
  )

  newPlantarApi.interceptors?.response.use(
    (response) => response?.data,
    interceptorResponseOnError
  )

  const currentAuthorization = () => authStore.loggedIn
    ? `${authStore.token.token_type} ${authStore.token.access_token}`
    : undefined

  /**
   * Renovação de token comum às instâncias que usam o token da sessão. O authStore.refresh
   * reaproveita a renovação em andamento, então 401 simultâneos em instâncias diferentes viram um
   * refresh só. O registro de acesso do refresh é feito pela própria API.
   */
  const refreshAuthLogic = async (failedRequest) => {
    if (failedRequest.response?.status !== 401) return Promise.reject()

    // 401 de uma requisição enviada antes do último refresh: o token já foi renovado, basta reenviar.
    if (wasSentWithOldToken(failedRequest, currentAuthorization())) return failedRequest

    try {
      if (await authStore.refresh()) return failedRequest

      authStore.logout()
    } catch (error) {
      authStore.logout()
    }

    return Promise.reject()
  }

  const refreshOptions = { pauseInstanceWhileRefreshing: false, interceptNetworkError: true }

  // Interceptadores de token expirado
  createAuthRefreshInterceptor(plantarApi, refreshAuthLogic, refreshOptions)
  createAuthRefreshInterceptor(newPlantarApi, refreshAuthLogic, refreshOptions)
  createAuthRefreshInterceptor(aliareApi.users, (failedRequest) => {
    if (failedRequest.code === 'ERR_CANCELED') return failedRequest

    return refreshAuthLogic(failedRequest)
  }, refreshOptions)
}
